/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印页面埋点 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-21 09:33:33
 */

import { useCallback } from 'react'
import { useAppSelector } from '../../../../app/hooks'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import {
  PhotoItem,
  WatermarkItem,
  WatermarkExtractionStatus
} from '../context/WatermarkBatchContext'

// 定义 pageType 类型
type PageType = 'allPhoto' | 'notEdit' | 'edited' | 'failEdit'

// 定义 actionType 类型
type ActionType =
  | 'enterPage'
  | 'remove'
  | 'switchPageType'
  | 'clickPhoto'
  | 'deletePhoto'
  | 'reExtractWatermark'
  | 'clickAddMore'
  | 'clickDownloadAll'
  | 'clickDownloadCurrentPhoto'
  | 'addItem'
  | 'clickApplyToCurrentPhoto'
  | 'clickApplyToSelectedPhotos'
  | 'clickApplyToAllPhotos'
  | 'showApplyToAllPhotosPop'
  | 'cancelApplyToAllPhotosPop'
  | 'confirmApplyToAllPhotosPop'
  | 'showProcessCompletePop'
  | 'clickCloseItem'
  | 'clickOpenItem'
  | 'back'
  | 'contactUs'
  | 'clickSurvey'
  | 'clickUsageGuide'
  | 'completeUsageGuide'
  | 'skipUsageGuide'
  | 'uploadToFolder'

// 埋点参数接口
interface TrackParams {
  actionType: ActionType
  pageType?: PageType
  groupID?: string
  role?: string
  number1?: number // 剩余编辑次数
  number2?: number // 全部照片数
  number3?: number // 未编辑照片数
  number4?: number // 水印提取失败照片数
  number5?: number // 处理成功照片数（actionType = showProcessCompletePop时上报）
  number6?: number // 处理失败照片数（actionType = showProcessCompletePop时上报）
  bool1?: boolean // 是否修改条目内容（actionType = clickApplyToCurrentPhoto,clickApplyToAllPhotos时上报）
  bool2?: boolean // 是否添加条目（actionType = clickApplyToCurrentPhoto,clickApplyToAllPhotos时上报）
  bool3?: boolean // 是否减少条目（actionType = clickApplyToCurrentPhoto,clickApplyToAllPhotos时上报）
  bool4?: boolean // 是否开启对比编辑前
  bool5?: boolean // 是否下载成功（actionType = clickDownloadAll,clickDownloadCurrentPhoto时上报）
  string1?: string // 新增条目内容详情（actionType = clickApplyToAllPhotos,cancelApplyToAllPhotos,confirmApplyToAllPhotos时上报）
  string2?: string // 修改条目内容详情（actionType = clickApplyToAllPhotos,cancelApplyToAllPhotos,confirmApplyToAllPhotos时上报）
  string3?: string // 删除条目内容详情（actionType = clickApplyToAllPhotos,cancelApplyToAllPhotos,confirmApplyToAllPhotos时上报）
  WatermarkID?: string // 记录水印的ID
  baseID?: string // 原始水印ID
  itemID?: string // 条目id（actionType = clickCloseItem,clickOpenItem时上报）
  photoNum?: number // 照片总数
}

/**
 * 批量编辑水印页面埋点 Hook
 * 返回埋点函数，自动填充公共参数（groupID、role等）
 */
export const useWatermarkBatchEditorTrack = () => {
  // 获取公共埋点参数
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const role = sensorsRole(myRole)

  /**
   * 批量编辑水印页面埋点函数
   */
  const trackWatermarkBatchEditor = useCallback(
    (params: Omit<TrackParams, 'groupID' | 'role'>) => {
      sensors.track('workgroup_b7748_click', {
        requirement_id: 845,
        track_sign: 'aworkgroup.b7748.click.workgroup_b7748_click',
        actionType: params.actionType,
        pageType: 'allPhoto',
        groupID: groupID || '',
        role,
        number1: params.number1,
        number2: params.number2,
        number3: params.number3,
        number4: params.number4,
        number5: params.number5,
        number6: params.number6,
        bool1: params.bool1,
        bool2: params.bool2,
        bool3: params.bool3,
        bool4: params.bool4 ?? false, // 默认传 false
        bool5: params.bool5,
        string1: params.string1,
        string2: params.string2,
        string3: params.string3,
        WatermarkID: params.WatermarkID,
        baseID: params.baseID,
        itemID: params.itemID,
        photoNum: params.photoNum
      })
    },
    [groupID, role]
  )

  return { trackWatermarkBatchEditor, groupID, role }
}

/**
 * 计算 pageType
 */
export const getPageType = (
  filterType: 'all' | 'unedited' | 'edited' | 'failed'
): PageType => {
  switch (filterType) {
    case 'all':
      return 'allPhoto'
    case 'unedited':
      return 'notEdit'
    case 'edited':
      return 'edited'
    case 'failed':
      return 'failEdit'
    default:
      return 'allPhoto'
  }
}

/**
 * 计算照片统计信息
 */
export const calculatePhotoStats = (photoList: PhotoItem[]) => {
  const allPhotoCount = photoList.length
  const uneditedCount = photoList.filter(
    (photo) =>
      photo.watermarkStatus === undefined ||
      photo.watermarkStatus === WatermarkExtractionStatus.COMPLETED ||
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING ||
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES
  ).length
  const editedCount = photoList.filter(
    (photo) =>
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
  ).length
  const failedExtractionCount = photoList.filter(
    (photo) =>
      photo.watermarkStatus === WatermarkExtractionStatus.FAILED ||
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING_TIMEOUT ||
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED
  ).length

  return {
    allPhotoCount,
    uneditedCount,
    editedCount,
    failedExtractionCount
  }
}

/**
 * 计算新增、修改、删除的条目详情
 */
export const calculateItemChanges = (
  originalItems: WatermarkItem[],
  editedItems: WatermarkItem[]
): {
  hasAdd: boolean
  hasModify: boolean
  hasDelete: boolean
  newItems: WatermarkItem[]
  modifiedItems: Array<{ item: WatermarkItem; originalContent: string }>
  deletedItems: WatermarkItem[]
  newItemsDetail: string
  modifiedItemsDetail: string
  deletedItemsDetail: string
} => {
  // 如果 editedItems 为空，则所有变更都为空
  if (!editedItems || editedItems.length === 0) {
    return {
      hasAdd: false,
      hasModify: false,
      hasDelete: false,
      newItems: [],
      modifiedItems: [],
      deletedItems: [],
      newItemsDetail: '',
      modifiedItemsDetail: '',
      deletedItemsDetail: ''
    }
  }

  // 找出新增的项目（编辑中有，原始中没有）
  const newItems = editedItems.filter(
    (editItem) =>
      editItem.switch &&
      !originalItems.some(
        (originalItem) => originalItem.item_id === editItem.item_id
      )
  )

  // 找出删除的项目（原始中有，编辑中关闭的）
  const deletedItems = originalItems.filter(
    (originalItem) =>
      !editedItems.some(
        (editItem) =>
          editItem.item_id === originalItem.item_id && editItem.switch
      )
  )

  // 找出修改的项目（原始和编辑中都有，但内容不同）
  const modifiedItems = editedItems
    .filter(
      (editItem) =>
        editItem.switch &&
        originalItems.some(
          (originalItem) =>
            originalItem.item_id === editItem.item_id &&
            originalItem.content !== editItem.content
        )
    )
    .map((editItem) => {
      const originalItem = originalItems.find(
        (original) => original.item_id === editItem.item_id
      )
      return {
        item: editItem,
        originalContent: originalItem?.content || '未设置内容'
      }
    })

  // 生成详情字符串
  const newItemsDetail = newItems
    .map((item) => `${item.title}:${item.content || '未设置内容'}`)
    .join('；')
  const modifiedItemsDetail = modifiedItems
    .map(
      (item) =>
        `${item.item.title}:${item.originalContent}→${
          item.item.content || '未设置内容'
        }`
    )
    .join('；')
  const deletedItemsDetail = deletedItems
    .map((item) => `${item.title}:${item.content || '未设置内容'}`)
    .join('；')

  return {
    hasAdd: newItems.length > 0,
    hasModify: modifiedItems.length > 0,
    hasDelete: deletedItems.length > 0,
    newItems,
    modifiedItems,
    deletedItems,
    newItemsDetail,
    modifiedItemsDetail,
    deletedItemsDetail
  }
}
